<template>
  <div>
    <div v-if="!hasErrorInAgreement" class="fla fla--fast-track content-width">
      <div>
        <h2>Series Common Field</h2>
        <SeriesCommonFieldForm ref="seriesCommonFieldForm" @passData="commonFieldFormRefs"/>
        <h2 style="margin-top:2em">Series Episode Field</h2>
        <SeriesEpisodesForm ref="seriesEpisodesForm" :value="formForEpisode"/>
        <el-form
            ref="termsForm"
            :model="form"
          >
            <LicenseRenderer
              licenseName="submission"
              @input="updateCheckBox"
              :form="{
                mediaRightsPerFilm: {
                  value: form.rights_media,
                },
                thirdParty: {
                  value: form.rights_general,
                },
                monetisation: {
                  value: form.rights_third_party_and_monetization,
                },
                labs:{
                  value: form.rights_labs
                }
              }"
            />

          </el-form>
        <div class="button__wrapper">
          <el-button
            type="primary"
            @click="submit(true)"
          >
            Save to draft
          </el-button>
            <el-popover
              placement="top-end"
              width="200"
              trigger="hover"
              content="You have to agree to the Terms and Conditions above first."
            >
              <span
                slot="reference"
                style="display: inline-block"
              >
                <el-button
                  type="primary"
                  @click="submit(false)"
                >
                  Submit
                </el-button>
              </span>
            </el-popover>
        </div>
      </div>

      <!-- Other related HTML codes -->
      <el-dialog
        :visible.sync="showEndDialogue"
        :show-close="false"
        :close-on-press-escape="false"
        :close-on-click-modal="false"
      >
        <div
          v-loading="loading"
          element-loading-text="Submitting..."
        >
          <SeriesThankYou
            :style="{ visibility: (loading) ? 'hidden' : 'visible' }" 
            :isDraft="isDraft"
          />
        </div>
      </el-dialog>
    </div>
    <Fallback v-else />
  </div>
</template>

<script>
import webcall from '@/libs/webcall';
import NOTIFY_CONSTANTS from '@/config/notify-constants';
import licenseParentMixin from '../mixins/licenseParentMixin';
// Others
import SeriesThankYou from '../Others/SeriesThankYou';
// Common
import LicenseRenderer from '../CommonLicenseComponents/LicenseRenderer';
// Components
import SeriesCommonFieldForm from '../Others/SeriesCommonFieldForm';
import SeriesEpisodesForm from '../Others/SeriesEpisodesForm';
import Terms from '../../Submission/steps/Terms';

export default {
  mixins: [
    licenseParentMixin,
  ],
  components: {
    LicenseRenderer,
    SeriesThankYou,
    SeriesCommonFieldForm,
    SeriesEpisodesForm,
    Terms,
  },
  data() {
    return {
      form: licenseParentMixin.functionalMethods.generateFormData(
        'form',
        {
          rights_general: '',
          rights_labs: 'LABS_INTERESTED',
          rights_third_party_and_monetization: 'THIRD_PARTY_AND_MONETIZATION',
          rights_media: 'MEDIA_ALL',
        },
      ),
      // General
      loading: false,
      showEndDialogue: false,
      isSubmitted: false,
      formForEpisode: {},
      isDraft: false,
    };
  },
  computed: {
    agreement() {
      return this.$store.state.agreement;
    },
    layout() {
      return this.$store.state.layout;
    },
  },
  methods: {
    handleLicenseUpdate([fieldname, fieldValue]) {
      // This will handle the update for primitive value
      // Complex object such as film details and filmmaker are handled by mutating inner properties of object
      // (This is not the best way, if you have time to implement a proper solution, consider doing it.)
      this.form[fieldname] = fieldValue;
    },

    commonFieldFormRefs(data) {
      this.formForEpisode = data;
    },

    submit(isDraft) {
      this.isDraft=isDraft;
      // Form to validate
      if (isDraft) {
        this.sendForm(isDraft);
      } else {
        const formValidationPromises = [
          this.$refs.seriesCommonFieldForm,
          this.$refs.seriesEpisodesForm,
        ].map(formInstance => formInstance.validate());
        // Check status
        Promise.all(formValidationPromises)
          .then(() => this.sendForm(isDraft))
          .catch((formInstance) => {
            if (formInstance instanceof Error) { // Denote network error. Not a validation error.
              this.$notify.error({
                title: 'Opps',
                message: formInstance.message,
                duration: NOTIFY_CONSTANTS.DURATION.LONG,
              });
              throw formInstance;
            }

            this.$notify.error({
              title: 'Opps',
              message: 'Some of the fields are empty or incorrectly filled.',
              duration: NOTIFY_CONSTANTS.DURATION.MEDIUM,
            });

            // Scroll element into view
            //  - These code are made to handle special cases where the element are hidden
            let node = formInstance.$el;
            let searchDepth = 5;
            while (node.clientWidth === 0 && searchDepth > 0) {
              // Assuming that parentNode always exist, since search will at most
              // reaches <html>, which should have width > 0
              node = node.parentNode;
              searchDepth -= 1;
            }
            if (node.clientWidth === 0) { node = document.body; }

            // Not all browser support scroll option. This effect will be applied progressively
            node.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
          });
      }
    },

    sendForm(isDraft) {
      this.showEndDialogue = true;
      this.loading = true;
      // Get the episodes field from the SeriesEpisodesForm
      const episodesForm=this.$refs.seriesEpisodesForm.episodes;
      // Get the common field from the SeriesCommonFieldForm
      const commonFieldForm=this.$refs.seriesCommonFieldForm.form;
      const films = episodesForm.map((episode) => {
        const result = { ...episode, ...commonFieldForm };
        // Remove the series title and description to concat with the episode's and other unused field
        delete result.series_title;
        delete result.series_description;
        delete result.contributors;
        delete result.directors;
        delete result.referral;
        // Add common title and description to the episode's title and description
        result.title=`${commonFieldForm.series_title ? commonFieldForm.series_title : ''}: ${result.title}`;
        result.description=`${commonFieldForm.series_description ? commonFieldForm.series_description : ''}\n\n${result.description}`;
        result.year = commonFieldForm.year ? commonFieldForm.year.getFullYear() : '';
        result.collaborators= [...episode.contributors, ...episode.directors];
        return result;
      });
      return webcall.submission.bulkFastTrack({
        ...this.form,
        films,
        is_draft: isDraft,
        referral: commonFieldForm.referral,
      })
        .then(() => {
          this.loading = false;
          this.isSubmitted = true;
        })
        .catch((error) => {
          this.loading = false;
          this.showEndDialogue = false;

          this.$notify.error({
            title: 'Error',
            message: error.data.message,
            duration: NOTIFY_CONSTANTS.DURATION.LONG,
          });
          throw error;
        });
    },
    getEditability(value) {
      // Once submitted, it can only be updated if it was previously unchecked.
      // I.e. user will not be able to untick this anymore once ticked.
      if (
        (value === 0) ||
        (value == null) ||
        (value === '') ||
        (value === false)
      ) return true;
      // Otherwise, user cannot update the rights anymore
      else return false;
    },
    updateCheckBox([prop, value]) {
      this.$emit('input', this.form[prop]=value);
    },
  },
};
</script>

<style lang="scss">
$negative-margin: $--main-padding * 0.75;

.fla__card {
  box-sizing: border-box;
  background-color: rgba(255,255,255,0.8);

  margin: auto #{-$negative-margin};
  @include breakpoint-up(xs) {
    margin: auto;
  }
}

.fla__body > * {
  margin-bottom: 3em;
}

.button__wrapper{
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 1em;
}

</style>



// WEBPACK FOOTER //
// src/components/Licenses/FilmLicenseAgreementFastTrack/SeriesFastTrack.vue